import { getSettings } from "src/services/configService";
import type { Settings } from "src/types/settings";

import { useCurrentWorkspace } from "@dashboard/workspaces/api/helpers";

export type WorkspaceSettings = {
    isEnrollmentPageEnabled: boolean;
    isOverviewPageEnabled: boolean;
    isDevicesPageEnabled: boolean;
    isGroupsPageEnabled: boolean;
    isProvisioningPageEnabled: boolean;
    isDownloadsPageEnabled: boolean;
    isProductsPageEnabled: boolean;
    isFilesPageEnabled: boolean;
    isRolloutPageEnabled: boolean;
    isOverviewPageShowingActivityChart: boolean;
    isDownloadsPageShowingPublicProducts: boolean;
    isMembersRequiringTwoFactorAuthentication: boolean;
};

export const workspaceSettingDefaults: Record<API.WorkspaceSettingName, boolean> = {
    EnrollmentPageEnabled: true,
    OverviewPageEnabled: true,
    DevicesPageEnabled: true,
    GroupsPageEnabled: true,
    ProvisioningPageEnabled: true,
    DownloadsPageEnabled: true,
    ProductsPageEnabled: true,
    FilesPageEnabled: true,
    RolloutPageEnabled: true,
    OverviewPageShowsActivityChart: true,
    DownloadsPageShowsPublicProducts: true,
    MembersRequireTwoFactorAuthentication: false,
};

export function getWorkspaceSettingValue(
    settings: readonly API.WorkspaceSetting[] | undefined,
    name: API.WorkspaceSettingName,
): boolean {
    const entry = settings?.find((s) => s.name === name);
    return entry === undefined ? workspaceSettingDefaults[name] : entry.value !== 0;
}

export function getRawWorkspaceSettings(settings: API.WorkspaceSetting[] | undefined): WorkspaceSettings {
    return {
        isEnrollmentPageEnabled: getWorkspaceSettingValue(settings, "EnrollmentPageEnabled"),
        isOverviewPageEnabled: getWorkspaceSettingValue(settings, "OverviewPageEnabled"),
        isDevicesPageEnabled: getWorkspaceSettingValue(settings, "DevicesPageEnabled"),
        isGroupsPageEnabled: getWorkspaceSettingValue(settings, "GroupsPageEnabled"),
        isProvisioningPageEnabled: getWorkspaceSettingValue(settings, "ProvisioningPageEnabled"),
        isDownloadsPageEnabled: getWorkspaceSettingValue(settings, "DownloadsPageEnabled"),
        isProductsPageEnabled: getWorkspaceSettingValue(settings, "ProductsPageEnabled"),
        isFilesPageEnabled: getWorkspaceSettingValue(settings, "FilesPageEnabled"),
        isRolloutPageEnabled: getWorkspaceSettingValue(settings, "RolloutPageEnabled"),
        isOverviewPageShowingActivityChart: getWorkspaceSettingValue(settings, "OverviewPageShowsActivityChart"),
        isDownloadsPageShowingPublicProducts: getWorkspaceSettingValue(settings, "DownloadsPageShowsPublicProducts"),
        isMembersRequiringTwoFactorAuthentication: getWorkspaceSettingValue(settings, "MembersRequireTwoFactorAuthentication"),
    };
}

export type EffectiveSettings = {
    isEnrollmentPageEnabled: boolean;
    isOverviewPageEnabled: boolean;
    isDevicesPageEnabled: boolean;
    isGroupsPageEnabled: boolean;
    isProvisioningPageEnabled: boolean;
    isDownloadsPageEnabled: boolean;
    isProductsPageEnabled: boolean;
    isFilesPageEnabled: boolean;
    isRolloutPageEnabled: boolean;
    isUniversalLicensesPageEnabled: boolean;
    isOverviewPageShowingActivityChart: boolean;
};

export function getEffectiveWorkspaceSettings(
    workspaceSettings: WorkspaceSettings,
    features: Settings["features"],
    workspaceExists: boolean,
): EffectiveSettings {
    const { pages } = features;

    if (!workspaceExists) {
        return {
            isEnrollmentPageEnabled: false,
            // A user with no workspace needs to reach Overview to create one, so don't hide it here.
            isOverviewPageEnabled: pages.overviewPageEnabled,
            isDevicesPageEnabled: false,
            isGroupsPageEnabled: false,
            isProvisioningPageEnabled: false,
            isDownloadsPageEnabled: false,
            isProductsPageEnabled: false,
            isFilesPageEnabled: false,
            isRolloutPageEnabled: false,
            isUniversalLicensesPageEnabled: false,
            isOverviewPageShowingActivityChart: false,
        };
    }

    const { eventChartEnabled } = features;
    return {
        isEnrollmentPageEnabled: pages.enrollmentPageEnabled && workspaceSettings.isEnrollmentPageEnabled,
        isOverviewPageEnabled: pages.overviewPageEnabled && workspaceSettings.isOverviewPageEnabled,
        isDevicesPageEnabled: pages.devicesPageEnabled && workspaceSettings.isDevicesPageEnabled,
        isGroupsPageEnabled: pages.groupsPageEnabled && workspaceSettings.isGroupsPageEnabled,
        isProvisioningPageEnabled: pages.provisioningPageEnabled && workspaceSettings.isProvisioningPageEnabled,
        isDownloadsPageEnabled: pages.downloadsPageEnabled && workspaceSettings.isDownloadsPageEnabled,
        isProductsPageEnabled: pages.productsPageEnabled && workspaceSettings.isProductsPageEnabled,
        isFilesPageEnabled: pages.filesPageEnabled && workspaceSettings.isFilesPageEnabled,
        isRolloutPageEnabled: pages.rolloutPageEnabled && workspaceSettings.isRolloutPageEnabled,
        isUniversalLicensesPageEnabled: pages.universalLicensesPageEnabled,
        isOverviewPageShowingActivityChart: eventChartEnabled && workspaceSettings.isOverviewPageShowingActivityChart,
    };
}

export function useAppSettingsService(): {
    rawWorkspaceSettings: WorkspaceSettings;
    effectiveWorkspaceSettings: EffectiveSettings;
    rawTenantSettings: Settings;
    isLoading: boolean;
    isFetching: boolean;
} {
    const { data: workspace, isLoading, isFetching } = useCurrentWorkspace();
    const rawTenantSettings = getSettings();

    const rawWorkspaceSettings = getRawWorkspaceSettings(workspace?.settings);

    const workspaceExists = workspace !== null && workspace !== undefined && !isLoading;
    const effectiveWorkspaceSettings = getEffectiveWorkspaceSettings(
        rawWorkspaceSettings,
        rawTenantSettings.features,
        workspaceExists,
    );

    return {
        rawWorkspaceSettings,
        effectiveWorkspaceSettings,
        rawTenantSettings,
        isLoading,
        isFetching,
    };
}
